'use client';

import { c as _c } from "react/compiler-runtime";
import { hashDiagnosticEntity, type QueryClient, setWalletAssetEntity, type UseQueryResult, useQueries, useQuery, useQueryClient, type WalletAssetEntity, walletAssetEntityObserverOptions } from '@morfi/backend-sdk-v2/client';
import { D6_SCALE, getUsdcMint } from '@/config/solana';
import type { Network } from '@/lib/network/types';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { deriveOutcomeTokenAta, deriveUsdcAta, parseTokenAccountBalance } from '@/lib/solana/ata';
import { reportError } from '@/lib/telemetry/client';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
export const TRADE_BALANCES_QUERY_KEY = 'trade-balances';
export const TRADE_BALANCES_STALE_MS = 6_000;
export const TRADE_BALANCES_REFETCH_MS = 5_000;
const MAX_ACCOUNTS_PER_RPC = 100;
export function useInvalidateTradeBalances() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: [TRADE_BALANCES_QUERY_KEY]
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export interface TradeBalanceAssetIdentity {
  readonly wallet: string;
  readonly mint: string;
}
export interface TradeBalances {
  readonly usdcBalance: number;
  readonly usdcRawBalance: string;
  readonly outcomeBalance: number;
  readonly outcomeRawBalance: string;
  readonly claimRightsBalance: number;
  readonly claimRightsRawBalance: string;
  readonly fetchedAt: number;
}
export interface TradeBalanceRequestMetadata {
  readonly fetchedAt: number;
  readonly slot: string;
}
export type TradeBalancesQueryResult = Omit<UseQueryResult<TradeBalanceRequestMetadata, Error>, 'data'> & {
  readonly data: TradeBalances | undefined;
  /**
   * The canonical quote-token balance is independent of the selected outcome.
   * It remains usable while a new outcome/claim-right ATA snapshot is loading.
   */
  readonly usdcBalance: number | undefined;
};
type FetchedTradeBalances = TradeBalances & TradeBalanceRequestMetadata;
async function fetchUsdcOnly(walletAddress: string, network: Network): Promise<FetchedTradeBalances> {
  const usdcAta = deriveUsdcAta(walletAddress);
  const response = await getConnection(network).getMultipleAccountsInfoAndContext([usdcAta]);
  const rawBalance = tokenAccountRawBalance(response.value[0]);
  return {
    usdcBalance: Number(rawBalance) / D6_SCALE,
    usdcRawBalance: rawBalance.toString(),
    outcomeBalance: 0,
    outcomeRawBalance: '0',
    claimRightsBalance: 0,
    claimRightsRawBalance: '0',
    fetchedAt: Date.now(),
    slot: response.context.slot.toString()
  };
}
export async function fetchTradeBalances(walletAddress: string, outcomeMint: string, claimRightsMint: string | undefined, network: Network): Promise<FetchedTradeBalances> {
  const usdcAta = deriveUsdcAta(walletAddress);
  const outcomeAta = deriveOutcomeTokenAta(walletAddress, outcomeMint);
  const claimRightsAta = claimRightsMint === undefined ? null : deriveOutcomeTokenAta(walletAddress, claimRightsMint);
  const response = await getConnection(network).getMultipleAccountsInfoAndContext(claimRightsAta === null ? [usdcAta, outcomeAta] : [usdcAta, outcomeAta, claimRightsAta]);
  const usdcRawBalance = tokenAccountRawBalance(response.value[0]);
  const outcomeRawBalance = tokenAccountRawBalance(response.value[1]);
  const claimRightsRawBalance = tokenAccountRawBalance(response.value[2]);
  return {
    usdcBalance: Number(usdcRawBalance) / D6_SCALE,
    usdcRawBalance: usdcRawBalance.toString(),
    outcomeBalance: Number(outcomeRawBalance) / D6_SCALE,
    outcomeRawBalance: outcomeRawBalance.toString(),
    claimRightsBalance: Number(claimRightsRawBalance) / D6_SCALE,
    claimRightsRawBalance: claimRightsRawBalance.toString(),
    fetchedAt: Date.now(),
    slot: response.context.slot.toString()
  };
}
export function tradeBalancesQueryOptions(queryClient: QueryClient, network: Network, walletAddress: string, outcomeMint: string, claimRightsMint?: string): {
  queryKey: readonly [typeof TRADE_BALANCES_QUERY_KEY, Network, string, string, string];
  queryFn: () => Promise<TradeBalanceRequestMetadata>;
  staleTime: number;
  refetchInterval: number;
  refetchOnWindowFocus: true;
} {
  return {
    queryKey: [TRADE_BALANCES_QUERY_KEY, network, walletAddress, outcomeMint, claimRightsMint ?? '__none__'],
    queryFn: async (): Promise<TradeBalanceRequestMetadata> => {
      const fetched = await fetchAndSeedTradeBalances(queryClient, network, walletAddress, outcomeMint, claimRightsMint);
      return {
        fetchedAt: fetched.fetchedAt,
        slot: fetched.slot
      };
    },
    staleTime: TRADE_BALANCES_STALE_MS,
    refetchInterval: TRADE_BALANCES_REFETCH_MS,
    refetchOnWindowFocus: true
  };
}

/**
 * One polling owner for every position asset in a holdings snapshot. Entity
 * observers retain per-wallet/mint reactivity while RPC completion produces a
 * single query transition instead of one transition per position.
 */
export function tradeBalanceAssetsBatchQueryOptions(queryClient: QueryClient, network: Network, requestedAssets: readonly TradeBalanceAssetIdentity[]): {
  queryKey: readonly [typeof TRADE_BALANCES_QUERY_KEY, 'batch', Network, string];
  queryFn: () => Promise<TradeBalanceRequestMetadata>;
  enabled: boolean;
  staleTime: number;
  refetchInterval: number;
  refetchOnWindowFocus: true;
} {
  const assets = canonicalTradeBalanceAssets(requestedAssets);
  return {
    queryKey: [TRADE_BALANCES_QUERY_KEY, 'batch', network, JSON.stringify(assets)],
    queryFn: async (): Promise<TradeBalanceRequestMetadata> => {
      const fetchedAt = Date.now();
      let latestSlot = 0;
      const chunks: TradeBalanceAssetIdentity[][] = [];
      for (let offset = 0; offset < assets.length; offset += MAX_ACCOUNTS_PER_RPC) {
        chunks.push(assets.slice(offset, offset + MAX_ACCOUNTS_PER_RPC));
      }
      const snapshots = await Promise.all(chunks.map(async chunk => ({
        chunk,
        response: await getConnection(network).getMultipleAccountsInfoAndContext(chunk.map(asset => deriveOutcomeTokenAta(asset.wallet, asset.mint)))
      })));
      for (const {
        chunk,
        response
      } of snapshots) {
        latestSlot = Math.max(latestSlot, response.context.slot);
        for (let index = 0; index < chunk.length; index += 1) {
          const asset = chunk[index];
          if (asset === undefined) continue;
          setWalletAssetEntity(queryClient, {
            network,
            ...asset,
            rawBalance: tokenAccountRawBalance(response.value[index]).toString(),
            fetchedAtMs: fetchedAt,
            slot: response.context.slot.toString(),
            source: 'trade_snapshot'
          }, logger);
        }
      }
      return {
        fetchedAt,
        slot: latestSlot.toString()
      };
    },
    enabled: assets.length > 0,
    staleTime: TRADE_BALANCES_STALE_MS,
    refetchInterval: TRADE_BALANCES_REFETCH_MS,
    refetchOnWindowFocus: true
  };
}
function canonicalTradeBalanceAssets(requestedAssets: readonly TradeBalanceAssetIdentity[]): TradeBalanceAssetIdentity[] {
  const byIdentity = new Map(requestedAssets.map(asset => [`${asset.wallet}:${asset.mint}`, {
    wallet: asset.wallet,
    mint: asset.mint
  }]));
  return [...byIdentity.values()].toSorted((left, right) => {
    const walletOrder = left.wallet.localeCompare(right.wallet);
    return walletOrder === 0 ? left.mint.localeCompare(right.mint) : walletOrder;
  });
}

/**
 * Batched balance hook for the trade form. This is the ONLY balance fetcher
 * the trade form should use — do not combine with standalone useWalletBalances
 * or useOutcomeTokenBalance in the same component.
 *
 * When outcomeMint is undefined (no outcome selected yet), fetches USDC only.
 * When an outcome is selected, fetches every execution-required canonical ATA
 * in one `getMultipleAccountsInfo` call.
 *
 * The initial snapshot is refreshed explicitly after a trade settles. Indexed
 * positions remain live through the positions subscription, so polling these
 * wallet accounts in the background only duplicates that realtime flow.
 *
 * Writes canonical wallet-asset entities so every balance consumer observes
 * the same fresh snapshot without an extra RPC call.
 */
export function useTradeBalances(walletAddress, outcomeMint, claimRightsMint) {
  const $ = _c(36);
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  const t0 = outcomeMint ?? "__none__";
  const t1 = claimRightsMint ?? "__none__";
  let t2;
  if ($[0] !== network || $[1] !== t0 || $[2] !== t1 || $[3] !== walletAddress) {
    t2 = [TRADE_BALANCES_QUERY_KEY, network, walletAddress, t0, t1];
    $[0] = network;
    $[1] = t0;
    $[2] = t1;
    $[3] = walletAddress;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== claimRightsMint || $[6] !== network || $[7] !== outcomeMint || $[8] !== queryClient || $[9] !== walletAddress) {
    t3 = async () => {
      if (walletAddress === undefined) {
        throw new Error("Trade balance wallet is unavailable");
      }
      ;
      try {
        const fetched = await fetchAndSeedTradeBalances(queryClient, network, walletAddress, outcomeMint, claimRightsMint);
        return {
          fetchedAt: fetched.fetchedAt,
          slot: fetched.slot
        };
      } catch (t4) {
        const error = t4;
        reportError(error, {
          component: "useTradeBalances",
          network,
          walletHash: hashDiagnosticEntity(walletAddress),
          outcomeMintHash: hashDiagnosticEntity(outcomeMint ?? "__none__"),
          claimRightsMintHash: hashDiagnosticEntity(claimRightsMint ?? "__none__")
        });
        throw error;
      }
    };
    $[5] = claimRightsMint;
    $[6] = network;
    $[7] = outcomeMint;
    $[8] = queryClient;
    $[9] = walletAddress;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = walletAddress !== undefined;
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: _temp,
      enabled: t4,
      staleTime: Number.POSITIVE_INFINITY,
      refetchInterval: false,
      refetchOnWindowFocus: false
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const request = useQuery(t5);
  let t6;
  let t7;
  if ($[15] !== claimRightsMint || $[16] !== network || $[17] !== outcomeMint || $[18] !== queryClient || $[19] !== walletAddress) {
    const mintIdentities = tradeBalanceMintIdentities(network, walletAddress, outcomeMint, claimRightsMint);
    t7 = useQueries;
    let t8;
    if ($[22] !== queryClient) {
      t8 = identity => walletAssetEntityObserverOptions(queryClient, identity);
      $[22] = queryClient;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t6 = mintIdentities.map(t8);
    $[15] = claimRightsMint;
    $[16] = network;
    $[17] = outcomeMint;
    $[18] = queryClient;
    $[19] = walletAddress;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  let t8;
  if ($[24] !== t6) {
    t8 = {
      queries: t6
    };
    $[24] = t6;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const entityQueries = t7(t8);
  let t9;
  let usdcBalance;
  if ($[26] !== claimRightsMint || $[27] !== entityQueries || $[28] !== outcomeMint || $[29] !== request.data) {
    const entities = entityQueries.map(_temp2);
    const usdcEntity = entities[0];
    usdcBalance = request.data === undefined || usdcEntity === undefined ? undefined : Number(BigInt(usdcEntity.rawBalance)) / D6_SCALE;
    t9 = request.data === undefined ? undefined : materializeTradeBalances(entities, outcomeMint, claimRightsMint, request.data.fetchedAt);
    $[26] = claimRightsMint;
    $[27] = entityQueries;
    $[28] = outcomeMint;
    $[29] = request.data;
    $[30] = t9;
    $[31] = usdcBalance;
  } else {
    t9 = $[30];
    usdcBalance = $[31];
  }
  const data = t9;
  let t10;
  if ($[32] !== data || $[33] !== request || $[34] !== usdcBalance) {
    t10 = {
      ...request,
      data,
      usdcBalance
    };
    $[32] = data;
    $[33] = request;
    $[34] = usdcBalance;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  return t10;
}
function _temp2(query) {
  return query.data as WalletAssetEntity | undefined;
}
function _temp(previous) {
  return previous;
}
export function tradeBalanceMintIdentities(network: Network, walletAddress: string | undefined, outcomeMint: string | undefined, claimRightsMint: string | undefined): readonly {
  readonly network: Network;
  readonly wallet: string;
  readonly mint: string;
}[] {
  if (walletAddress === undefined) return [];
  const identities = [{
    network,
    wallet: walletAddress,
    mint: getUsdcMint(network)
  }];
  if (outcomeMint !== undefined) identities.push({
    network,
    wallet: walletAddress,
    mint: outcomeMint
  });
  if (claimRightsMint !== undefined) {
    identities.push({
      network,
      wallet: walletAddress,
      mint: claimRightsMint
    });
  }
  return identities;
}
async function fetchAndSeedTradeBalances(queryClient: QueryClient, network: Network, walletAddress: string, outcomeMint: string | undefined, claimRightsMint: string | undefined): Promise<FetchedTradeBalances> {
  const fetched = outcomeMint === undefined ? await fetchUsdcOnly(walletAddress, network) : await fetchTradeBalances(walletAddress, outcomeMint, claimRightsMint, network);
  const identities = tradeBalanceMintIdentities(network, walletAddress, outcomeMint, claimRightsMint);
  const rawBalances = [fetched.usdcRawBalance, fetched.outcomeRawBalance, fetched.claimRightsRawBalance];
  for (let index = 0; index < identities.length; index += 1) {
    const identity = identities[index];
    const rawBalance = rawBalances[index];
    if (identity === undefined || rawBalance === undefined) continue;
    setWalletAssetEntity(queryClient, {
      ...identity,
      rawBalance,
      fetchedAtMs: fetched.fetchedAt,
      slot: fetched.slot,
      source: 'trade_snapshot'
    }, logger);
  }
  return fetched;
}
function materializeTradeBalances(entities: readonly (WalletAssetEntity | undefined)[], outcomeMint: string | undefined, claimRightsMint: string | undefined, fetchedAt: number): TradeBalances | undefined {
  const usdc = entities[0];
  const outcome = outcomeMint === undefined ? undefined : entities[1];
  const claimRights = claimRightsMint === undefined ? undefined : entities[2];
  if (usdc === undefined || outcomeMint !== undefined && outcome === undefined || claimRightsMint !== undefined && claimRights === undefined) {
    return undefined;
  }
  const outcomeRawBalance = outcome?.rawBalance ?? '0';
  const claimRightsRawBalance = claimRights?.rawBalance ?? '0';
  return {
    usdcBalance: Number(BigInt(usdc.rawBalance)) / D6_SCALE,
    usdcRawBalance: usdc.rawBalance,
    outcomeBalance: Number(BigInt(outcomeRawBalance)) / D6_SCALE,
    outcomeRawBalance,
    claimRightsBalance: Number(BigInt(claimRightsRawBalance)) / D6_SCALE,
    claimRightsRawBalance,
    fetchedAt
  };
}
function tokenAccountRawBalance(account: {
  readonly data: Buffer;
} | null | undefined): bigint {
  return account === null || account === undefined ? BigInt(0) : parseTokenAccountBalance(account.data);
}